import {Vector3} from 'three';

// Allocation-free picking field. Keeping 27-element bindings for every vertex
// of eight models would cost much more memory than the cages themselves.
const coordinates=new Float64Array(3),middle=new Int32Array(3),weights=new Float64Array(9);
export function samplePilePoint(body,point,out){
  for(let axis=0;axis<3;axis++){
    coordinates[axis]=(point[axis]-body.min[axis])*3/(body.max[axis]-body.min[axis]);
    middle[axis]=Math.floor(coordinates[axis]+.5);out[axis]=point[axis];
    for(let j=-1;j<=1;j++){const d=Math.abs(coordinates[axis]-middle[axis]-j);weights[axis*3+j+1]=d<.5?.75-d*d:.5*(1.5-d)**2;}
  }
  for(let z=-1;z<=1;z++)for(let y=-1;y<=1;y++)for(let x=-1;x<=1;x++){
    const index=(Math.max(0,Math.min(3,middle[0]+x))+4*(Math.max(0,Math.min(3,middle[1]+y))+4*Math.max(0,Math.min(3,middle[2]+z))))*3;
    const weight=weights[x+1]*weights[3+y+1]*weights[6+z+1];
    for(let axis=0;axis<3;axis++)out[axis]+=(body.p[index+axis]-body.initialRest[index+axis])*weight;
  }
  return out;
}

// The CPU solves 64 cage nodes. The GPU applies the same quadratic field as
// SoftBody.bind to vertices and transports authored normals with its Jacobian.
export function attachPileSurface(material,body){
  const displacement={value:new Float32Array(64*3)};
  const uniforms={pileDisplacement:displacement,pileMin:{value:new Vector3(...body.min)},pileGridScale:{value:new Vector3(...body.max.map((value,axis)=>3/(value-body.min[axis])))}};
  const previous=material.onBeforeCompile;
  material.onBeforeCompile=shader=>{
    previous.call(material,shader);Object.assign(shader.uniforms,uniforms);
    shader.vertexShader=`
uniform vec3 pileDisplacement[64];
uniform vec3 pileMin;
uniform vec3 pileGridScale;
void pileField(vec3 source, out vec3 displaced, out mat3 jacobian){
  vec3 t=(source-pileMin)*pileGridScale;
  ivec3 middle=ivec3(floor(t+0.5));
  displaced=source;jacobian=mat3(1.0);
  for(int z=-1;z<=1;z++)for(int y=-1;y<=1;y++)for(int x=-1;x<=1;x++){
    ivec3 grid=middle+ivec3(x,y,z);
    vec3 delta=t-vec3(grid),distance=abs(delta);
    vec3 inner=vec3(1.0)-step(vec3(0.5),distance);
    vec3 weight=mix(0.5*pow(vec3(1.5)-distance,vec3(2.0)),vec3(0.75)-distance*distance,inner);
    vec3 derivative=mix(-(vec3(1.5)-distance)*sign(delta),-2.0*delta,inner)*pileGridScale;
    ivec3 safeGrid=clamp(grid,ivec3(0),ivec3(3));
    vec3 d=pileDisplacement[safeGrid.x+4*(safeGrid.y+4*safeGrid.z)];
    displaced+=d*weight.x*weight.y*weight.z;
    jacobian[0]+=d*derivative.x*weight.y*weight.z;
    jacobian[1]+=d*weight.x*derivative.y*weight.z;
    jacobian[2]+=d*weight.x*weight.y*derivative.z;
  }
}
`+shader.vertexShader;
    shader.vertexShader=shader.vertexShader.replace('#include <beginnormal_vertex>',`#include <beginnormal_vertex>
      vec3 pilePosition;mat3 pileJacobian;pileField(position,pilePosition,pileJacobian);
      objectNormal=normalize(mat3(cross(pileJacobian[1],pileJacobian[2]),cross(pileJacobian[2],pileJacobian[0]),cross(pileJacobian[0],pileJacobian[1]))*objectNormal);`);
    shader.vertexShader=shader.vertexShader.replace('#include <begin_vertex>','vec3 transformed=pilePosition;');
  };
  material.customProgramCacheKey=()=>`pile-cage-v1:${material.name}`;
  return {update(){for(let i=0;i<body.p.length;i++)displacement.value[i]=body.p[i]-body.initialRest[i];},uniforms};
}
